import { computed, onMounted } from 'vue'
import { consumeNavFeedback } from '@/composables/useDashboardNavFeedback'
import { useStore } from 'vuex'
import { useDonorPlan } from '@/composables/useDonorPlan'
import { useDonorFinancialYears } from '@/composables/useDonorFinancialYears'
import { getCurrentUkTaxYear } from '@/helpers/ukTaxYear'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'

export type DashboardHomeState = 'free' | 'paid'

/**
 * Loads donations, then selects the dashboard home layout.
 */
export function useDashboardHome() {
  const store = useStore()
  const { isPaid, isPlanKnown } = useDonorPlan()

  const {
    sortedYears,
    isReady: donationsReady,
    isLoading: donationsLoading,
    apiError: donationsError,
    currentYear,
    loadDonations,
  } = useDonorFinancialYears({ autoLoad: false })

  const token = computed(() => store.getters.token as string | null)

  const isReady = computed(() => isPlanKnown.value && donationsReady.value)

  const isPageLoading = computed(
    () => !isPlanKnown.value || donationsLoading.value || !donationsReady.value
  )

  const apiError = computed(() => donationsError.value)

  /** Paid home always needs a current tax year row, zeroed when the donor has no gifts yet. */
  const paidCurrentYear = computed((): DonorFinancialYear => {
    if (currentYear.value) return currentYear.value
    return {
      period: getCurrentUkTaxYear().periodKey,
      donations: [],
      totalDonations: 0,
      totalGiftAid: 0,
    }
  })

  const homeState = computed((): DashboardHomeState | null => {
    if (!isReady.value) return null
    if (isPaid.value) return 'paid'
    return 'free'
  })

  onMounted(async () => {
    consumeNavFeedback()
    if (token.value === null) {
      await new Promise((r) => setTimeout(r, 2500))
    }
    await loadDonations()
    store.commit('SET_LOADING', false)
  })

  return {
    homeState,
    isPageLoading,
    apiError,
    sortedYears,
    currentYear,
    paidCurrentYear,
  }
}
